import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings, Check, Shield, BarChart3, Target, Eye } from '../lib/icons';

const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: true,
    marketing: true,
    functional: true
  });

  useEffect(() => {
    // Direkt entscheiden, ob der Banner gezeigt werden muss — der frühere
    // setTimeout(1000) kostet Lighthouse-Performance, weil das Banner-DOM
    // verzögert mountet und dadurch als "verspätetes LCP-Element" gewertet wird.
    const hasConsent = localStorage.getItem('cookieConsent');
    if (!hasConsent) {
      setShowBanner(true);
    } else {
      try {
        const savedPrefs = JSON.parse(hasConsent);
        setPreferences(prev => ({ ...prev, ...savedPrefs }));
      } catch (e) {
        // If parsing fails, use defaults
      }
    }
  }, []);

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setShowBanner(false);
    setShowSettings(false);
    
    // Simulate analytics event (but don't actually block anything)
    console.log('🍪 Cookie consent: All cookies accepted');
  };

  const acceptSelected = () => {
    const selectedPrefs = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(selectedPrefs));
    setShowBanner(false);
    setShowSettings(false);
    
    // Simulate analytics event (but don't actually block anything)
    console.log('🍪 Cookie consent: Custom preferences saved', selectedPrefs);
  };

  const rejectAll = () => {
    const rejected = {
      necessary: true, // Necessary cookies cannot be rejected
      analytics: false,
      marketing: false,
      functional: false,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(rejected));
    setShowBanner(false);
    setShowSettings(false);
    
    // Simulate analytics event (but don't actually block anything)
    console.log('🍪 Cookie consent: All optional cookies rejected');
  };

  const togglePreference = (key: keyof typeof preferences) => {
    if (key === 'necessary') return; // Cannot disable necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner — auf Mobile bewusst kompakt gehalten (1-zeilig, kein
          Icon, kürzerer Text), damit der Banner nicht zum LCP-Element wird.
          Größere Layout-Variante erst ab sm:. */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2.5 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
            <div className="flex items-center sm:items-start gap-2 sm:gap-4 min-w-0">
              <div className="hidden sm:flex flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Cookie className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="hidden sm:block text-lg font-semibold text-gray-900 mb-2">
                  Wir verwenden Cookies
                </h3>
                {/* Mobile: kurz, einzeilig. Lighthouse-LCP-relevant. */}
                <p className="sm:hidden text-gray-700 text-xs leading-snug">
                  Wir nutzen Cookies.{' '}
                  <a href="/datenschutz" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">
                    Datenschutz lesen
                  </a>
                </p>
                {/* Desktop/Tablet: ausführlicher. */}
                <p className="hidden sm:block text-gray-600 text-sm leading-relaxed">
                  Wir nutzen Cookies und ähnliche Technologien, um Ihnen die bestmögliche Nutzererfahrung zu bieten,
                  unsere Website zu verbessern und relevante Inhalte anzuzeigen. Durch die weitere Nutzung stimmen Sie
                  der Verwendung von Cookies zu.{' '}
                  <a
                    href="/datenschutz"
                    className="text-blue-600 hover:text-blue-800 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Mehr erfahren
                  </a>
                </p>
              </div>
            </div>

            <div className="flex flex-row gap-2 sm:gap-3 sm:flex-shrink-0">
              <button
                onClick={() => setShowSettings(true)}
                className="flex items-center justify-center px-2.5 sm:px-4 py-1.5 sm:py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-xs sm:text-sm"
                aria-label="Cookie-Einstellungen öffnen"
              >
                <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">Einstellungen</span>
              </button>
              <button
                onClick={rejectAll}
                className="flex items-center justify-center px-2.5 sm:px-4 py-1.5 sm:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium text-xs sm:text-sm"
                aria-label="Nur notwendige Cookies akzeptieren"
              >
                <span className="sm:hidden">Ablehnen</span>
                <span className="hidden sm:inline">Nur notwendige</span>
              </button>
              <button
                onClick={acceptAll}
                className="flex items-center justify-center px-3 sm:px-6 py-1.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-xs sm:text-sm"
                aria-label="Alle Cookies akzeptieren"
              >
                <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
                <span>Akzeptieren</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Cookie className="w-6 h-6 mr-3 text-blue-600" />
                  Cookie-Einstellungen
                </h2>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-gray-600 p-2"
                  aria-label="Cookie-Einstellungen schließen"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <p className="text-gray-600 mb-8 leading-relaxed">
                Wir verwenden verschiedene Arten von Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. 
                Sie können Ihre Präferenzen hier anpassen. Beachten Sie, dass das Deaktivieren bestimmter Cookies die Funktionalität 
                der Website beeinträchtigen kann.
              </p>

              <div className="space-y-6">
                {/* Necessary Cookies */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <Shield className="w-6 h-6 text-green-600 mr-3" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Notwendige Cookies</h3>
                        <p className="text-sm text-gray-600">Erforderlich für die Grundfunktionen der Website</p>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-gray-500 mr-3">Immer aktiv</span>
                      <div className="w-12 h-6 bg-green-500 rounded-full flex items-center justify-end px-1">
                        <div className="w-4 h-4 bg-white rounded-full shadow-sm"></div>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Diese Cookies sind für das ordnungsgemäße Funktionieren der Website unerlässlich. 
                    Sie ermöglichen grundlegende Funktionen wie Seitennavigation, Zugriff auf sichere Bereiche 
                    und die Speicherung Ihrer Cookie-Präferenzen.
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <BarChart3 className="w-6 h-6 text-blue-600 mr-3" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Analyse-Cookies</h3>
                        <p className="text-sm text-gray-600">Helfen uns, die Website zu verbessern</p>
                      </div>
                    </div>
                    <button
                      onClick={() => togglePreference('analytics')}
                      className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                        preferences.analytics ? 'bg-blue-500 justify-end' : 'bg-gray-300 justify-start'
                      }`}
                      aria-label={`Analyse-Cookies ${preferences.analytics ? 'deaktivieren' : 'aktivieren'}`}
                      aria-checked={preferences.analytics}
                      role="switch"
                    >
                      <div className="w-4 h-4 bg-white rounded-full shadow-sm mx-1"></div>
                    </button>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Diese Cookies sammeln anonyme Informationen darüber, wie Besucher unsere Website nutzen. 
                    Sie helfen uns zu verstehen, welche Seiten am beliebtesten sind und wie sich Besucher auf der Website bewegen.
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <Target className="w-6 h-6 text-purple-600 mr-3" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Marketing-Cookies</h3>
                        <p className="text-sm text-gray-600">Für personalisierte Werbung und Inhalte</p>
                      </div>
                    </div>
                    <button
                      onClick={() => togglePreference('marketing')}
                      className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                        preferences.marketing ? 'bg-purple-500 justify-end' : 'bg-gray-300 justify-start'
                      }`}
                      aria-label={`Marketing-Cookies ${preferences.marketing ? 'deaktivieren' : 'aktivieren'}`}
                      aria-checked={preferences.marketing}
                      role="switch"
                    >
                      <div className="w-4 h-4 bg-white rounded-full shadow-sm mx-1"></div>
                    </button>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Diese Cookies werden verwendet, um Ihnen relevante Werbung und personalisierte Inhalte zu zeigen. 
                    Sie können auch dazu verwendet werden, die Wirksamkeit unserer Werbekampagnen zu messen.
                  </p>
                </div>

                {/* Functional Cookies */}
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <Eye className="w-6 h-6 text-orange-600 mr-3" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Funktionale Cookies</h3>
                        <p className="text-sm text-gray-600">Für erweiterte Website-Funktionen</p>
                      </div>
                    </div>
                    <button
                      onClick={() => togglePreference('functional')}
                      className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                        preferences.functional ? 'bg-orange-500 justify-end' : 'bg-gray-300 justify-start'
                      }`}
                      aria-label={`Funktionale Cookies ${preferences.functional ? 'deaktivieren' : 'aktivieren'}`}
                      aria-checked={preferences.functional}
                      role="switch"
                    >
                      <div className="w-4 h-4 bg-white rounded-full shadow-sm mx-1"></div>
                    </button>
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Diese Cookies ermöglichen erweiterte Funktionen und Personalisierung. Sie können von uns oder 
                    von Drittanbietern gesetzt werden, deren Dienste wir auf unseren Seiten verwenden.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={rejectAll}
                  className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-300 transition-colors font-medium"
                  aria-label="Nur notwendige Cookies akzeptieren"
                >
                  Nur notwendige
                </button>
                <button
                  onClick={acceptSelected}
                  className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors font-medium"
                  aria-label="Ausgewählte Cookie-Einstellungen speichern"
                >
                  Auswahl speichern
                </button>
                <button
                  onClick={acceptAll}
                  className="flex-1 hover:bg-green-800 transition-colors font-medium"
                  aria-label="Alle Cookies akzeptieren"
                >
                  Alle akzeptieren
                </button>
              </div>

              {/* Legal Links */}
              <div className="mt-6 pt-4 border-t border-gray-200 text-center">
                <div className="flex flex-wrap justify-center gap-4 text-sm">
                  <a 
                    href="/datenschutz" 
                    className="text-blue-600 hover:text-blue-800 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Datenschutzerklärung
                  </a>
                  <a 
                    href="/impressum" 
                    className="text-blue-600 hover:text-blue-800 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Impressum
                  </a>
                  <a 
                    href="/agb" 
                    className="text-blue-600 hover:text-blue-800 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    AGB
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop for modal */}
      {showSettings && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setShowSettings(false)}
        />
      )}
    </>
  );
};

export default CookieConsent;